import type { NewRestaurant, Restaurant } from "@/types";
import { deriveTasteFields } from "@/lib/rating";
import { cloudWriteAdd, cloudWriteRemove, cloudWriteUpdate } from "@/lib/sync";
import { getCurrentUser } from "@/lib/firebase";
import { normalizeAreaRemote } from "@/lib/areas";

export const STORAGE_KEY = "ccq:restaurants";

function isHouseholdMode(): boolean {
  try {
    const id = (import.meta.env.VITE_CCQ_HOUSEHOLD_ID as string | undefined)?.trim();
    return !!id && id.length > 0;
  } catch {
    return false;
  }
}

function read(): Restaurant[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    const arr: Restaurant[] = Array.isArray(parsed) ? parsed : [];
    if (isHouseholdMode()) {
      // Household共有: household全体で共通の isWishlist を使う
      return arr.map((r) => {
        const by = Array.isArray(r.wishlistBy) ? (r.wishlistBy as string[]) : [];
        const w = typeof r.isWishlist === 'boolean' ? r.isWishlist : by.length > 0;
        return { ...r, isWishlist: w } as Restaurant;
      });
    }
    // 従来のユーザ別モード: 自分のUIDに基づいて isWishlist を算出
    const uid = getCurrentUser()?.uid;
    if (!uid) return arr;
    return arr.map((r) => ({
      ...r,
      isWishlist: Array.isArray(r.wishlistBy) ? (r.wishlistBy as string[]).includes(uid) : !!r.isWishlist,
    } as Restaurant));
  } catch (_e) {
    return [];
  }
}

function write(data: Restaurant[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  // 同一タブでもUIが更新されるよう、疑似ストレージイベントを通知
  try {
    const ev: any = typeof StorageEvent !== 'undefined'
      ? new StorageEvent('storage', { key: STORAGE_KEY })
      : new Event('storage');
    (ev as any).key = (ev as any).key || STORAGE_KEY;
    window.dispatchEvent(ev);
  } catch { /* noop */ }
  try { window.dispatchEvent(new CustomEvent('ccq:restaurants-changed')); } catch { /* noop */ }
}

function genId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function hasVisitEvidence(input: Partial<Restaurant>): boolean {
  const scoreFields: (keyof Restaurant)[] = [
    "rating",
    "ratingTaste",
    "ratingTasteBalance",
    "ratingTasteIngredients",
    "ratingTasteTechnique",
    "ratingTasteFine",
    "ratingAmbience",
    "ratingService",
    "ratingCost",
  ];
  const hasScore = scoreFields.some((key) => {
    const value = Number(input[key]);
    return Number.isFinite(value) && value > 0;
  });
  const hasDate = typeof input.visitDate === "string" && input.visitDate.trim().length > 0;
  const hasHistory = Array.isArray(input.visitHistory) && input.visitHistory.filter(Boolean).length > 0;
  return hasScore || hasDate || hasHistory;
}

export function getRestaurants(): Restaurant[] {
  return read();
}

export function addRestaurant(input: NewRestaurant): Restaurant {
  const now = new Date().toISOString();
  const id = input.id ?? genId();
  const me = getCurrentUser();
  const uid = me?.uid;
  const inHH = isHouseholdMode();
  // wishlistBy: householdでも個人の希望を保持する
  const initialWishlistBy = Array.isArray((input as any).wishlistBy) ? ((input as any).wishlistBy as string[]).slice() : [];
  if (input.isWishlist && uid && !initialWishlistBy.includes(uid)) initialWishlistBy.push(uid);
  // visitHistory: 最大100件に制限し、新しい順に（既に来ているなら重複排除）
  const hist = Array.isArray((input as any).visitHistory) ? ((input as any).visitHistory as string[]).filter(Boolean) : [];
  const vh = Array.from(new Set(hist)).slice(0, 100);
  const lastVisited = vh[0] || input.visitDate || undefined;
  const tastePatch = deriveTasteFields(input as Partial<Restaurant>);
  const visited = hasVisitEvidence({ ...(input as Partial<Restaurant>), ...tastePatch, visitDate: lastVisited, visitHistory: vh });
  const wishlistBy = visited ? [] : initialWishlistBy;
  const item: Restaurant = {
    ...(input as any),
    ...tastePatch,
    id,
    createdAt: now,
    updatedAt: now,
    createdByUid: uid,
    createdByName: me?.displayName || me?.email || undefined,
    createdByEmail: me?.email || undefined,
    isWishlist: visited ? false : (inHH ? wishlistBy.length > 0 : !!input.isWishlist),
    wishlistBy,
    visitHistory: vh.length > 0 ? vh : undefined,
    visitDate: lastVisited,
  } as Restaurant;
  const list = read();
  list.unshift(item);
  write(list);
  try { void cloudWriteAdd(item); } catch (_e) { /* ignore: optional cloud write */ }
  return item;
}

export function updateRestaurant(id: string, patch: Partial<Restaurant>): Restaurant | undefined {
  const list = read();
  const idx = list.findIndex((r) => r.id === id);
  if (idx === -1) return undefined;
  const now = new Date().toISOString();
  const current = list[idx];
  const me = getCurrentUser();
  const uid = me?.uid;
  const next: Restaurant = { ...current, ...patch } as Restaurant;
  const inHH = isHouseholdMode();
  // wishlistBy の更新（householdでも個人希望を保持）。トグル実行者のUIDのみを増減。
  if (typeof patch.isWishlist === 'boolean' && uid) {
    const set = new Set<string>(Array.isArray(current.wishlistBy) ? (current.wishlistBy as string[]) : []);
    if (patch.isWishlist) set.add(uid); else set.delete(uid);
    next.wishlistBy = Array.from(set);
  }
  // household の見かけの isWishlist は wishlistBy の有無で決める
  if (inHH) {
    next.isWishlist = Array.isArray(next.wishlistBy) && next.wishlistBy.length > 0;
  }
  // 訪問履歴の正規化（最大100件、新しい順）と最終訪問日の更新
  if (Array.isArray((next as any).visitHistory)) {
    const cleaned = ((next as any).visitHistory as string[]).filter(Boolean);
    const uniq = Array.from(new Set(cleaned));
    (next as any).visitHistory = uniq.slice(0, 100);
    if (uniq.length > 0) next.visitDate = uniq[0];
  }
  const tastePatch = deriveTasteFields(next);
  let updated = { ...next, ...tastePatch, updatedAt: now } as Restaurant;
  if (hasVisitEvidence(updated)) {
    updated = { ...updated, isWishlist: false, wishlistBy: [] } as Restaurant;
  }
  list[idx] = updated;
  write(list);
  try { void cloudWriteUpdate(id, updated); } catch (_e) { /* ignore: optional cloud write */ }
  return updated;
}

export function removeRestaurant(id: string) {
  const list = read();
  const next = list.filter((r) => r.id !== id);
  write(next);
  try { void cloudWriteRemove(id); } catch (_e) { /* ignore: optional cloud write */ }
}

// One-off cleanup: remove a known broken record by exact or prefix match
// This is safe to call multiple times; it will be a no-op after removal.
export function cleanupBrokenRecords() {
  const TARGET = '1756220467428';
  const list = read();
  if (!Array.isArray(list) || list.length === 0) return;
  const filtered = list.filter((r) => !(r && typeof r.id === 'string' && (r.id === TARGET || r.id.startsWith(TARGET))));
  if (filtered.length !== list.length) {
    write(filtered);
  }
}

export async function normalizeRestaurantAreas(): Promise<{ updated: number; total: number }> {
  const list = read();
  let updated = 0;
  const next: Restaurant[] = [];
  for (const item of list) {
    try {
      const area = await normalizeAreaRemote({
        name: item.name,
        location: item.location,
        prefecture: item.prefecture,
        municipality: item.municipality,
      });
      const patch: Partial<Restaurant> = {};
      if (area.prefecture && area.prefecture !== item.prefecture) patch.prefecture = area.prefecture;
      if (area.municipality && area.municipality !== item.municipality) patch.municipality = area.municipality;
      if (Object.keys(patch).length > 0) {
        const merged = { ...item, ...patch, updatedAt: new Date().toISOString() } as Restaurant;
        next.push(merged);
        updated += 1;
        try { void cloudWriteUpdate(item.id, merged); } catch (_e) { /* ignore: optional cloud write */ }
      } else {
        next.push(item);
      }
    } catch {
      next.push(item);
    }
  }
  if (updated > 0) write(next);
  return { updated, total: list.length };
}
